import { useState, useMemo, useEffect } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, TrendingUp, Clock, Target, Sparkles, ArrowUpRight, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TurnstileWidget from "./TurnstileWidget";

// FX snapshot — December 2025. Edit these as needed; values are approximate and only used for ROI estimation.
// rateToZar = how many ZAR 1 unit of this currency is worth.
const CURRENCIES = [
    { code: "ZAR", symbol: "R",   flag: "🇿🇦", name: "South African Rand", rateToZar: 1,        locale: "en-ZA", dealMin: 10000,    dealMax: 500000,    dealStep: 5000,    dealDefault: 75000 },
    { code: "USD", symbol: "$",   flag: "🇺🇸", name: "US Dollar",          rateToZar: 18.5,     locale: "en-US", dealMin: 500,      dealMax: 50000,     dealStep: 500,     dealDefault: 5000 },
    { code: "EUR", symbol: "€",   flag: "🇪🇺", name: "Euro",               rateToZar: 20.0,     locale: "en-IE", dealMin: 500,      dealMax: 50000,     dealStep: 500,     dealDefault: 5000 },
    { code: "GBP", symbol: "£",   flag: "🇬🇧", name: "Pound Sterling",     rateToZar: 23.0,     locale: "en-GB", dealMin: 500,      dealMax: 50000,     dealStep: 500,     dealDefault: 5000 },
    { code: "NGN", symbol: "₦",   flag: "🇳🇬", name: "Nigerian Naira",     rateToZar: 0.012,    locale: "en-NG", dealMin: 500000,   dealMax: 100000000, dealStep: 500000,  dealDefault: 7000000 },
    { code: "KES", symbol: "KSh", flag: "🇰🇪", name: "Kenyan Shilling",    rateToZar: 0.14,     locale: "en-KE", dealMin: 50000,    dealMax: 10000000,  dealStep: 50000,   dealDefault: 700000 },
    { code: "EGP", symbol: "E£",  flag: "🇪🇬", name: "Egyptian Pound",     rateToZar: 0.39,     locale: "en-EG", dealMin: 25000,    dealMax: 3000000,   dealStep: 25000,   dealDefault: 250000 },
    { code: "GHS", symbol: "₵",   flag: "🇬🇭", name: "Ghanaian Cedi",      rateToZar: 1.5,      locale: "en-GH", dealMin: 5000,     dealMax: 500000,    dealStep: 5000,    dealDefault: 50000 },
    { code: "MAD", symbol: "DH",  flag: "🇲🇦", name: "Moroccan Dirham",    rateToZar: 1.85,     locale: "fr-MA", dealMin: 5000,     dealMax: 500000,    dealStep: 5000,    dealDefault: 50000 },
    { code: "BWP", symbol: "P",   flag: "🇧🇼", name: "Botswana Pula",      rateToZar: 1.3,      locale: "en-BW", dealMin: 7000,     dealMax: 500000,    dealStep: 5000,    dealDefault: 60000 },
    { code: "MUR", symbol: "₨",   flag: "🇲🇺", name: "Mauritian Rupee",    rateToZar: 0.42,     locale: "en-MU", dealMin: 20000,    dealMax: 2000000,   dealStep: 20000,   dealDefault: 200000 },
];

const fmt = (amount, currency) =>
    new Intl.NumberFormat(currency.locale, {
        style: "currency",
        currency: currency.code,
        maximumFractionDigits: 0,
    }).format(amount || 0);

const fmtCompact = (amount, currency) => {
    if (!Number.isFinite(amount)) return fmt(0, currency);
    if (Math.abs(amount) >= 1_000_000) return new Intl.NumberFormat(currency.locale, { style: "currency", currency: currency.code, maximumFractionDigits: 1, notation: "compact" }).format(amount);
    if (Math.abs(amount) >= 10_000) return new Intl.NumberFormat(currency.locale, { style: "currency", currency: currency.code, maximumFractionDigits: 0, notation: "compact" }).format(amount);
    return fmt(amount, currency);
};

const WIN_RATE_LIFT = 0.20;
const MIN_PER_CALL_SAVED = 12;

export default function RoiCalculator() {
    const [currencyCode, setCurrencyCode] = useState("ZAR");
    const currency = CURRENCIES.find((c) => c.code === currencyCode) || CURRENCIES[0];

    const [reps, setReps] = useState([8]);
    const [callsPerRep, setCallsPerRep] = useState([15]);
    const [avgDeal, setAvgDeal] = useState([currency.dealDefault]);
    const [winRate, setWinRate] = useState([20]);

    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [token, setToken] = useState("");
    const [touched, setTouched] = useState(false); // toggles the "Sample" badge

    const markTouched = () => { if (!touched) setTouched(true); };

    // When the user changes currency, snap the deal slider to that currency's default range.
    useEffect(() => {
        setAvgDeal([currency.dealDefault]);
    }, [currencyCode, currency.dealDefault]);

    const r = reps[0], cpr = callsPerRep[0], deal = avgDeal[0], wr = winRate[0];

    const calc = useMemo(() => {
        const callsPerYear = r * cpr * 12;
        const currentRevenue = callsPerYear * (wr / 100) * deal;
        const liftedRevenue = callsPerYear * ((wr * (1 + WIN_RATE_LIFT)) / 100) * deal;
        const revenueLift = Math.max(0, liftedRevenue - currentRevenue);

        const hoursSaved = (callsPerYear * MIN_PER_CALL_SAVED) / 60;

        // Plan cost is published in ZAR — convert to the user's currency for display & ROI math.
        let monthlyZar, recommendedPlan;
        if (r <= 3)       { monthlyZar = 1299; recommendedPlan = "Pro"; }
        else if (r <= 10) { monthlyZar = 4499; recommendedPlan = "Business"; }
        else              { monthlyZar = 4499 + (r - 10) * 349; recommendedPlan = "Business + seats"; }

        const monthlyCost = monthlyZar / currency.rateToZar;
        const annualCost = monthlyCost * 12;
        const paybackMonths = revenueLift > 0 ? Math.min(99, annualCost / (revenueLift / 12)) : 99;
        const roiMultiple = revenueLift / Math.max(1, annualCost);

        return { callsPerYear, revenueLift, hoursSaved, annualCost, monthlyCost, paybackMonths, roiMultiple, recommendedPlan };
    }, [r, cpr, deal, wr, currency.rateToZar]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error("Enter a work email to receive your report.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/roi-leads", {
                email: email.trim(),
                reps: r,
                calls_per_rep: cpr,
                avg_deal_amount: deal,
                currency: currency.code,
                win_rate: wr,
                calculated_revenue_lift_amount: Math.round(calc.revenueLift),
                turnstile_token: token || undefined,
            });
            setSubmitted(true);
            toast.success("Report sent — check your inbox.");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not send report. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="roi-calculator" data-testid="roi-calculator" className="bg-white border-y border-zinc-200 relative scroll-mt-20">
            <div className="absolute inset-x-0 top-0 h-[400px] bg-[radial-gradient(ellipse_60%_50%_at_50%_0%,rgba(37,99,235,0.06),transparent_70%)]" aria-hidden />
            <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-20 md:py-28">
                <div className="max-w-2xl">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">ROI Calculator</p>
                    <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        How much pipeline is poor qualification costing you?
                    </h2>
                    <p className="mt-4 text-lg text-zinc-600">
                        Drag the sliders. We'll model the revenue lift, time saved, and payback period
                        for your team — based on a conservative {Math.round(WIN_RATE_LIFT * 100)}% win-rate lift seen by PipeGuide customers.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Inputs */}
                    <div className="lg:col-span-5 rounded-2xl border border-zinc-200 bg-white p-7 md:p-8 shadow-sm">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <h3 className="font-display text-lg font-semibold text-zinc-900">Your team</h3>
                                {!touched && (
                                    <span data-testid="roi-sample-badge" className="inline-flex items-center gap-1 rounded-full bg-brand-tint border border-brand-muted text-brand-hover text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5">
                                        <span className="h-1.5 w-1.5 rounded-full bg-brand-primary animate-pulse" />
                                        Sample
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-zinc-500">Currency</span>
                                <Select value={currencyCode} onValueChange={(v) => { markTouched(); setCurrencyCode(v); }}>
                                    <SelectTrigger data-testid="roi-currency" className="h-9 w-[150px] text-sm">
                                        <SelectValue>
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="text-base leading-none">{currency.flag}</span>
                                                <span className="font-mono-pg text-xs font-semibold">{currency.code}</span>
                                            </span>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[320px]">
                                        {CURRENCIES.map((c) => (
                                            <SelectItem key={c.code} value={c.code} data-testid={`roi-currency-${c.code}`}>
                                                <span className="inline-flex items-center gap-2">
                                                    <span className="text-base leading-none">{c.flag}</span>
                                                    <span className="font-mono-pg text-xs font-semibold w-10">{c.code}</span>
                                                    <span className="text-xs text-zinc-500">{c.name}</span>
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="mt-6 space-y-7">
                            <SliderRow
                                testId="roi-reps"
                                label="Sales reps"
                                value={r}
                                display={`${r}`}
                                onChange={(v) => { markTouched(); setReps(v); }}
                                min={1} max={50} step={1}
                                minDisplay="1" maxDisplay="50"
                            />
                            <SliderRow
                                testId="roi-calls"
                                label="Qualification calls / rep / month"
                                value={cpr}
                                display={`${cpr}`}
                                onChange={(v) => { markTouched(); setCallsPerRep(v); }}
                                min={3} max={40} step={1}
                                minDisplay="3" maxDisplay="40"
                            />
                            <SliderRow
                                testId="roi-deal"
                                label="Average deal size"
                                value={deal}
                                display={fmtCompact(deal, currency)}
                                onChange={(v) => { markTouched(); setAvgDeal(v); }}
                                min={currency.dealMin} max={currency.dealMax} step={currency.dealStep}
                                minDisplay={fmtCompact(currency.dealMin, currency)}
                                maxDisplay={fmtCompact(currency.dealMax, currency)}
                            />
                            <SliderRow
                                testId="roi-winrate"
                                label="Current win rate"
                                value={wr}
                                display={`${wr}%`}
                                onChange={(v) => { markTouched(); setWinRate(v); }}
                                min={5} max={60} step={1}
                                minDisplay="5%" maxDisplay="60%"
                            />
                        </div>

                        {!touched && (
                            <p data-testid="roi-sample-hint" className="mt-4 text-xs text-brand-hover/80 italic">
                                These are sample inputs — drag any slider to model your team.
                            </p>
                        )}

                        <div className="mt-7 pt-6 border-t border-zinc-200 flex items-center gap-2 text-xs text-zinc-500">
                            <Sparkles className="h-3.5 w-3.5 text-brand-primary" />
                            Total qualification calls / year:&nbsp;
                            <span className="font-mono-pg text-zinc-900 font-semibold">
                                {calc.callsPerYear.toLocaleString(currency.locale)}
                            </span>
                        </div>
                    </div>

                    {/* Results */}
                    <div className="lg:col-span-7 rounded-2xl border border-brand-primary/20 bg-gradient-to-br from-brand-tint/40 via-white to-white p-7 md:p-9 shadow-sm relative overflow-hidden">
                        <div className="absolute -right-12 -top-12 h-48 w-48 rounded-full bg-brand-primary/5 blur-3xl" aria-hidden />
                        <div className="relative">
                            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Your projected impact</p>

                            <div data-testid="roi-disclaimer" className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3.5 py-2.5">
                                <Info className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" aria-hidden />
                                <p className="text-[12px] leading-snug text-amber-900">
                                    <span className="font-semibold">These are estimates only</span> — directional figures based on industry averages.
                                    A precise ROI projection requires a detailed exercise with your real pipeline data.{" "}
                                    <a href="/contact" className="underline decoration-amber-400 underline-offset-2 hover:text-amber-950">Talk to us</a> to run one.
                                </p>
                            </div>

                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <ResultBig
                                    testId="roi-result-revenue"
                                    icon={TrendingUp}
                                    label="Annual revenue lift"
                                    value={fmtCompact(calc.revenueLift, currency)}
                                    sub="from a 20% win-rate lift"
                                    primary
                                />
                                <ResultBig
                                    testId="roi-result-hours"
                                    icon={Clock}
                                    label="Hours saved / year"
                                    value={Math.round(calc.hoursSaved).toLocaleString(currency.locale)}
                                    sub={`${MIN_PER_CALL_SAVED} min × ${calc.callsPerYear.toLocaleString(currency.locale)} calls`}
                                />
                                <ResultSmall
                                    testId="roi-result-payback"
                                    label="Payback period"
                                    value={calc.paybackMonths >= 99 ? "—" : `${calc.paybackMonths < 1 ? "<1" : Math.ceil(calc.paybackMonths)} month${Math.ceil(calc.paybackMonths) === 1 ? "" : "s"}`}
                                />
                                <ResultSmall
                                    testId="roi-result-roi"
                                    label="Return on PipeGuide"
                                    value={calc.roiMultiple >= 1 ? `${calc.roiMultiple.toFixed(1)}×` : "<1×"}
                                />
                            </div>

                            <div className="mt-5 rounded-xl border border-zinc-200 bg-white p-4 flex items-start gap-3">
                                <Target className="h-4 w-4 mt-0.5 text-brand-primary" />
                                <div className="flex-1">
                                    <p className="text-sm text-zinc-700">
                                        Recommended plan: <span className="font-semibold text-zinc-900">{calc.recommendedPlan}</span>
                                        <span className="font-mono-pg text-zinc-500 ml-2">
                                            · {fmtCompact(calc.monthlyCost, currency)}/mo · {fmtCompact(calc.annualCost, currency)}/yr
                                        </span>
                                    </p>
                                    {currency.code !== "ZAR" && (
                                        <p className="text-[11px] text-zinc-400 mt-1">
                                            PipeGuide pricing is published in ZAR. Converted at ~R{currency.rateToZar} = 1 {currency.code} (Dec 2025 snapshot).
                                        </p>
                                    )}
                                </div>
                            </div>

                            {!submitted ? (
                                <form onSubmit={handleSubmit} data-testid="roi-form" className="mt-6 space-y-3">
                                    <p className="text-sm text-zinc-700 font-medium">Email yourself the full breakdown</p>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <div className="relative flex-1">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                                            <input
                                                type="email"
                                                required
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@company.com"
                                                data-testid="roi-email"
                                                className="w-full h-11 rounded-lg bg-white border border-zinc-300 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                            />
                                        </div>
                                        <Button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="roi-submit-btn"
                                            className="bg-brand-primary hover:bg-brand-hover text-white h-11 px-5 font-medium"
                                        >
                                            {submitting ? "Sending…" : <>Email my report <ArrowUpRight className="ml-1 h-4 w-4" /></>}
                                        </Button>
                                    </div>
                                    <TurnstileWidget onToken={setToken} />
                                    <p className="text-xs text-zinc-500">
                                        We'll send your inputs + calculations to your inbox. No spam, just your numbers.
                                    </p>
                                </form>
                            ) : (
                                <div data-testid="roi-success" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-semibold text-emerald-900">Report on its way to {email}</p>
                                        <p className="text-xs text-emerald-800 mt-1">A member of our team may follow up to walk you through the assumptions.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <p className="mt-6 text-xs text-zinc-400 max-w-3xl">
                    * Estimates use a conservative 20% win-rate lift seen across PipeGuide customers in their first 90 days.
                    Actual results vary by team maturity and qualification baseline. Time savings assume {MIN_PER_CALL_SAVED} minutes / call avoided on note-taking & CRM admin.
                    Cross-currency figures use a December 2025 FX snapshot — final billing is in ZAR.
                </p>
            </div>
        </section>
    );
}

const SliderRow = ({ testId, label, value, display, onChange, min, max, step, minDisplay, maxDisplay }) => (
    <div data-testid={testId}>
        <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-zinc-700">{label}</label>
            <span className="font-mono-pg text-sm font-semibold text-zinc-900 tabular-nums">{display}</span>
        </div>
        <Slider value={[value]} min={min} max={max} step={step} onValueChange={onChange} />
        <div className="flex justify-between mt-1.5 text-[10px] text-zinc-400 font-mono-pg">
            <span>{minDisplay}</span>
            <span>{maxDisplay}</span>
        </div>
    </div>
);

const ResultBig = ({ testId, icon: Icon, label, value, sub, primary }) => (
    <div data-testid={testId} className={`rounded-xl border p-5 ${primary ? "border-brand-primary/30 bg-white shadow-[0_8px_30px_-12px_rgba(37,99,235,0.25)]" : "border-zinc-200 bg-white"}`}>
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
            <Icon className={`h-3.5 w-3.5 ${primary ? "text-brand-primary" : "text-zinc-500"}`} />
            {label}
        </div>
        <p className={`mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight tabular-nums ${primary ? "text-brand-primary" : "text-zinc-900"}`}>
            {value}
        </p>
        {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
);

const ResultSmall = ({ testId, label, value }) => (
    <div data-testid={testId} className="rounded-xl border border-zinc-200 bg-white p-5">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">{label}</p>
        <p className="mt-2 font-display text-2xl font-bold text-zinc-900 tabular-nums">{value}</p>
    </div>
);
